/**
 * Resolución canónica de la "Marca" de un item de pedido/solicitud.
 *
 * La Marca es la columna BD `article_references.category` (p. ej. "EXTRA") —
 * exactamente lo que la página de artículos ya muestra como Marca. No confundir
 * con la familia/categoría del artículo.
 *
 * Cadena de fallbacks (con `||` deliberado: cadena vacía degrada al siguiente):
 *  1. `item.item.article_reference` | `item.article_reference` — estructura
 *     anidada (array u objeto) que traen las solicitudes → `category || name`.
 *  2. `item.item.category` — `RecommendedArticleDTO.Category`: el caso real de
 *     los pedidos cargados para revisión/edición (`providers[].items[].item`).
 *  3. `item.category` — snapshot top-level de `PurchaseOrderItem` (defensivo).
 *  4. `""` — sin marca. NUNCA "-": el valor alimenta la clave de sort/búsqueda
 *     de las tablas; el guion visual lo pinta {@link getArticleBrandDisplay}.
 *
 * Contrato: los DTOs del API garantizan `category`/`name` como string; si un
 * origen enviara otro tipo truthy se devolvería tal cual (sin normalizar).
 *
 * @param {*} item - Item del carrito/pedido en cualquiera de sus formas; los
 *   tipos no-objeto (string, número, array, null, undefined) devuelven `""`.
 * @returns {string} La marca resuelta, o `""` si no hay dato en ninguna forma.
 */
export function getArticleBrand(item) {
	if (!item || typeof item !== "object" || Array.isArray(item)) return "";

	const refs = item.item?.article_reference || item.article_reference;
	if (Array.isArray(refs) && refs.length > 0) {
		const brand = refs[0]?.category || refs[0]?.name;
		if (brand) return brand;
	} else if (refs && typeof refs === "object") {
		const brand = refs.category || refs.name;
		if (brand) return brand;
	}

	return item.item?.category || item.category || "";
}

/**
 * Variante de presentación de {@link getArticleBrand}: la marca resuelta o el
 * guion `"-"` para celdas sin dato. Úsala en plantillas; para claves de
 * sort/búsqueda usa siempre `getArticleBrand` (vacío ≠ guion).
 *
 * @param {*} item - Mismo contrato de entrada que {@link getArticleBrand}.
 * @returns {string} La marca, o `"-"` si no hay.
 */
export function getArticleBrandDisplay(item) {
	return getArticleBrand(item) || "-";
}
